// Theme controller, adapted from careers.co.jp's src/lib/theme.ts. Persists
// "bigfive.theme" in localStorage and writes the matching data-theme
// attribute on <html> (globals.css keys every color variable off it). The
// same storage key is read by the inline anti-flash script in index.html
// before React boots, so the first paint already uses the saved theme.
// Unlike the parent, this site defaults to dark.

import { useCallback, useEffect, useState } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "bigfive.theme";
const DEFAULT: Theme = "dark";

export function getStoredTheme(): Theme {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : DEFAULT;
  } catch {
    // localStorage can throw (Safari private mode) — fall back to dark.
    return DEFAULT;
  }
}

function setStoredTheme(t: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, t);
  } catch {
    // Storage unavailable; the live attribute still updates so the current
    // tab works, the choice just won't survive a refresh.
  }
}

function applyTheme(t: Theme): void {
  document.documentElement.dataset.theme = t;
}

export interface ThemeApi {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

export function useTheme(): ThemeApi {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());

  useEffect(() => {
    applyTheme(theme);
    setStoredTheme(theme);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(
    () => setThemeState((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  return { theme, setTheme, toggle };
}
